import { useMemo } from "react";
import { t } from "ttag";

import { getShortStrategyLabel } from "metabase/admin/performance/utils";
import { MaybeLink } from "metabase/common/components/Badge/Badge.styled";
import { Ellipsified } from "metabase/common/components/Ellipsified";
import { EllipsifiedCollectionPath } from "metabase/common/components/EllipsifiedPath/EllipsifiedCollectionPath";
import Link from "metabase/common/components/Link";
import { getIcon } from "metabase/lib/icon";
import * as Urls from "metabase/lib/urls";
import { Box, Button, FixedSizeIcon, Flex, Icon } from "metabase/ui";
import type { CacheRefreshStatus, CacheableModel } from "metabase-types/api";

import StrategyEditorForQuestionsAndDashboardsS from "./StrategyEditorForQuestionsAndDashboards.module.css";
import { getItemUrl } from "./item-utils";
import type { CacheableItem, UpdateTarget } from "./types";

const formatNextRunAt = (nextRunAt?: string | null) => {
  if (!nextRunAt) {
    return "-";
  }

  const date = new Date(nextRunAt);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRefreshStatusLabel = (status: CacheRefreshStatus["status"]) => {
  switch (status) {
    case "running":
      return t`Running`;
    case "success":
      return t`Success`;
    case "failed":
      return t`Failed`;
  }
};

const formatRefreshStatus = (refreshStatus?: CacheRefreshStatus | null) => {
  if (!refreshStatus) {
    return "-";
  }

  const label = getRefreshStatusLabel(refreshStatus.status);
  return refreshStatus.duration_ms != null
    ? `${label} (${refreshStatus.duration_ms}ms)`
    : label;
};

export const TableRowForCacheableItem = ({
  item,
  forId,
  currentTargetId,
  currentTargetModel,
  updateTarget,
  isFormDirty,
  onRefreshNow,
  isRefreshNowPending,
}: {
  item: CacheableItem;
  forId: number;
  currentTargetId: number | null;
  currentTargetModel: CacheableModel | null;
  updateTarget: UpdateTarget;
  isFormDirty: boolean;
  onRefreshNow: (item: CacheableItem) => void;
  isRefreshNowPending: boolean;
}) => {
  const { name, id, collection, model, strategy, iconModel } = item;
  const refreshStatus = item.refresh_status;
  const autoRefresh =
    strategy?.type === "duration" || strategy?.type === "schedule"
      ? strategy.refresh_automatically
      : false;
  const canRefreshNow =
    strategy?.type === "duration" || strategy?.type === "schedule";
  const isRefreshRunning = refreshStatus?.status === "running";

  const iconName = iconModel
    ? getIcon({ model: iconModel || "card" }).name
    : null;

  const url = useMemo(
    () => getItemUrl(model, item as { id: number; name: string }) || undefined,
    [model, item],
  );

  const launchForm = () => {
    if (currentTargetId !== item.id || currentTargetModel !== item.model) {
      updateTarget({ id, model }, isFormDirty);
    }
  };

  return (
    <tr
      className={
        currentTargetId !== null && currentTargetId === forId
          ? StrategyEditorForQuestionsAndDashboardsS.currentTarget
          : undefined
      }
    >
      <td>
        <MaybeLink
          className={StrategyEditorForQuestionsAndDashboardsS.ItemLink}
          to={url}
        >
          <Flex
            align="center"
            wrap="nowrap"
            gap="sm"
            className={StrategyEditorForQuestionsAndDashboardsS.ItemNameCell}
          >
            {iconName ? (
              <FixedSizeIcon name={iconName} />
            ) : (
              <Box h="sm" w="md" />
            )}
            <Ellipsified fw="bold">{name}</Ellipsified>
          </Flex>
        </MaybeLink>
      </td>
      <td>
        {collection && (
          <Link
            className={StrategyEditorForQuestionsAndDashboardsS.CollectionLink}
            to={Urls.collection(collection)}
          >
            <EllipsifiedCollectionPath collection={collection} />
          </Link>
        )}
      </td>
      <td>
        <Button variant="subtle" onClick={() => launchForm()} p={0} fw="bold">
          {getShortStrategyLabel(strategy)}
        </Button>
      </td>
      <td className={StrategyEditorForQuestionsAndDashboardsS.CompactCell}>
        {autoRefresh ? t`On` : t`Off`}
      </td>
      <td
        className={StrategyEditorForQuestionsAndDashboardsS.CompactCell}
        title={item.next_run_at ?? undefined}
      >
        {formatNextRunAt(item.next_run_at)}
      </td>
      <td
        className={StrategyEditorForQuestionsAndDashboardsS.CompactCell}
        title={refreshStatus?.error ?? undefined}
      >
        <span
          className={
            refreshStatus?.status === "failed"
              ? StrategyEditorForQuestionsAndDashboardsS.FailedStatus
              : undefined
          }
        >
          {formatRefreshStatus(refreshStatus)}
        </span>
      </td>
      <td className={StrategyEditorForQuestionsAndDashboardsS.ActionsCell}>
        <Button
          size="xs"
          variant="subtle"
          leftSection={<Icon name="refresh" />}
          loading={isRefreshNowPending}
          disabled={!canRefreshNow || isRefreshRunning}
          onClick={() => onRefreshNow(item)}
        >
          {t`Refresh now`}
        </Button>
      </td>
    </tr>
  );
};
